import { isEmailIdentifier } from '../api/sessions'

/**
 * The address the forgot-password form opens with. It arrives one of two ways.
 *
 * A failed sign-in hands it over through sessionStorage, never the link: an
 * address in a URL lands in browser history, Webflow's server logs, referrers
 * and every marketing tag on the page, and the login page controls both ends
 * of that trip. It lives in the tab for one navigation and is removed the
 * moment the form reads it. Only an email is carried — a child's username is
 * no use on a page that needs the parent's address.
 *
 * A link from outside the site — the create-password link a new parent
 * follows after checkout — has nothing but the URL to carry it, so `?email=`
 * is honoured too, and wins when present.
 */
const KEY = 'cv.forgot-email.v1'

export const EMAIL_PARAM = 'email'

/** Called as the forgot link is followed. A username is left behind. */
export function rememberEmailForReset(identifier: string): void {
  const email = identifier.trim()
  if (!isEmailIdentifier(email)) return
  try {
    sessionStorage.setItem(KEY, email)
  } catch {
    // Storage access throws in private-mode Safari and when storage is
    // blocked. The parent types the address again; nothing else is lost.
  }
}

/**
 * The address to open the form with: the link's, else the one a failed
 * sign-in left behind. The memory is cleared either way, so a stale address
 * never surfaces on a later visit.
 */
export function takeEmailForReset(search: string): string {
  const remembered = takeRemembered()
  return new URLSearchParams(search).get(EMAIL_PARAM)?.trim() || remembered
}

function takeRemembered(): string {
  try {
    const email = sessionStorage.getItem(KEY) ?? ''
    sessionStorage.removeItem(KEY)
    return email
  } catch {
    return ''
  }
}
